import { Typography } from "@mui/material";
import { CalculatorMode } from "@verity/database/prisma";
import { CoreInfoIndicator, RadioOption } from "../../../core";
import i18nPackage from "../../../i18n/i18n";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { useProctoringHelpers } from "../hooks/useProctoringHelpers";
import { RadioGroupSubItem } from "../option-menus/radioGroupSubItem";
import OptionBlock from "../optionBlock";
import styles from "./index.module.css";

const ScientificRadioOption = () => {
	return (
		<div className={styles.scientificContainer}>
			<Typography>{i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientific")}</Typography>
			<CoreInfoIndicator
				hint={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientificTooltip")}
				size={20}
			/>
		</div>
	);
};

function CalculatorOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const { enableCalculator } = proctoringOption;
	const calculatorAllowed = isOptionAllowed("enableCalculator");

	const calcRadioOptions: RadioOption[] = [
		{
			value: "standard",
			label: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.standard"),
			disabled: !calculatorAllowed,
		},
		{
			value: "scientific",
			label: <ScientificRadioOption />,
			disabled: !calculatorAllowed,
		},
	];

	return (
		<OptionBlock
			checked={enableCalculator !== "off"}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableCalculator: checked ? "standard" : "off",
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
			optionKey="enableCalculator"
			menuItems={
				<RadioGroupSubItem
					radioGroupDescription={i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.subtitle")}
					radioOptions={calcRadioOptions}
					onChange={(_, value) => {
						setProctoringOption({
							...proctoringOption,
							enableCalculator: value as CalculatorMode,
						});
					}}
					selectedValue={enableCalculator}
				/>
			}
		/>
	);
}

function WhiteboardOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const { enableWhiteboard } = proctoringOption;

	return (
		<OptionBlock
			optionKey="enableWhiteboard"
			checked={Boolean(enableWhiteboard)}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableWhiteboard: checked,
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
		/>
	);
}

function CodeEditorOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const { enableCodeEditor } = proctoringOption;

	function generateRoomId(length = 6) {
		const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		let roomId = "";
		for (let i = 0; i < length; i++) {
			roomId += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return roomId;
	}

	return (
		<OptionBlock
			optionKey="enableCodeEditor"
			checked={Boolean(enableCodeEditor)}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					enableCodeEditor: checked,
					codeEditorRoomId: checked ? [generateRoomId()] : null,
					showRecordingPanel: checked ? true : proctoringOption.showRecordingPanel,
				});
			}}
		/>
	);
}

export default function ShowToolsOptions() {
	const { shouldShowOption } = useProctoringHelpers();
	return (
		<>
			{shouldShowOption("enableCalculator") && <CalculatorOption />}
			{shouldShowOption("enableWhiteboard") && <WhiteboardOption />}
			{shouldShowOption("enableCodeEditor") && <CodeEditorOption />}
		</>
	);
}
